import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
  Calendar,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  XCircle,
  Filter,
  RefreshCw,
} from 'lucide-react';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const TABS = [
  { key: 'performance', label: 'Performance' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'conversions', label: 'Conversions' },
];

const DATE_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom Range' },
];

const STATUS_OPTIONS = ['All Status', 'Pending', 'Approved', 'Rejected'];
const PAID_OPTIONS = ['All Paid', 'Paid', 'Unpaid'];
const ROWS_PER_PAGE = 10;

const AdminReports = () => {
  const [activeTab, setActiveTab] = useState('performance');
  const [dateRange, setDateRange] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [paidFilter, setPaidFilter] = useState('All Paid');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // ==================== FETCH ====================
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const params = new URLSearchParams();
      params.append('dateRange', dateRange);
      if (dateRange === 'custom') {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      const response = await axios.get(
        `${baseUrl}/api/admin/reports/${activeTab}?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.status) {
        setData(response.data.data || []);
        setCurrentPage(1);
      } else {
        toast.error(response.data?.msg || 'Failed to load reports');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error(error.response?.data?.msg || 'Failed to load reports');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, dateRange, startDate, endDate]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ==================== FILTERS (client-side) ====================
  const filteredData = useMemo(() => {
    if (activeTab === 'performance') return data;

    return data.filter((row) => {
      // Status filter
      if (statusFilter !== 'All Status') {
        if ((row.valid || '').toLowerCase() !== statusFilter.toLowerCase()) return false;
      }
      // Paid filter
      if (paidFilter !== 'All Paid') {
        const isPaid = String(row.paid) === '1';
        if (paidFilter === 'Paid' && !isPaid) return false;
        if (paidFilter === 'Unpaid' && isPaid) return false;
      }
      return true;
    });
  }, [data, activeTab, statusFilter, paidFilter]);

  // ==================== PAGINATION ====================
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const paginatedData = useMemo(() => {
    if (activeTab === 'performance') return filteredData;
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredData.slice(start, start + ROWS_PER_PAGE);
  }, [filteredData, currentPage, activeTab]);

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  // ==================== EXPORT HELPERS ====================
  const getExportRows = () => {
    if (activeTab === 'performance') {
      return filteredData.map((r) => ({
        Offer: r.offer_name,
        'Offer ID': r.offer_id,
        Clicks: r.clicks,
        Unique: r.unique_clicks,
        Conversions: r.conversion,
        'CR %': `${r.cr_rate}%`,
        Earnings: `₹${r.earning}`,
      }));
    }
    if (activeTab === 'clicks') {
      return filteredData.map((r) => ({
        TID: r.tid,
        Offer: r.offer_name,
        User: r.user_name || r.user_upi,
        UPI: r.user_upi,
        Event: r.event_name,
        Status: r.valid,
        Paid: r.paid === '1' || r.paid === 1 ? 'Yes' : 'No',
        Device: r.device,
        OS: r.os,
        Browser: r.browser,
        Time: r.click_time,
      }));
    }
    // conversions
    return filteredData.map((r) => ({
      TID: r.tid,
      Offer: r.offer_name,
      User: r.user_name || r.user_upi,
      UPI: r.user_upi,
      Event: r.event_name,
      Amount: `₹${r.user_amount}`,
      Status: r.valid,
      Paid: r.paid === '1' || r.paid === 1 ? 'Yes' : 'No',
      'Click Time': r.click_time,
      'Conv Time': r.conv_time,
    }));
  };

  const handleExportCSV = () => {
    const rows = getExportRows();
    if (!rows.length) {
      toast.error('No data to export');
      return;
    }
    const headers = Object.keys(rows[0]);
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab}_report_${dateRange}_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('CSV exported successfully');
  };

  const handleExportPDF = () => {
    // Simple browser-print to PDF. Replace with jsPDF if you have it installed.
    toast.info('Use "Print → Save as PDF" from the browser to export.');
    setTimeout(() => window.print(), 300);
  };

  // ==================== RENDER HELPERS ====================
  const renderAvatar = (logo, name) => (
    <div className="w-10 h-10 rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden shrink-0">
      {logo ? (
        <img
          src={logo}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
              name || 'O'
            )}&background=059669&color=fff`;
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-600">
          <Filter className="w-4 h-4" />
        </div>
      )}
    </div>
  );

  const statusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-black uppercase">
          <CheckCircle2 className="w-3 h-3" /> Approved
        </span>
      );
    }
    if (s === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg text-[10px] font-black uppercase">
          <XCircle className="w-3 h-3" /> Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[10px] font-black uppercase">
        Pending
      </span>
    );
  };

  // ==================== DATE RANGE LABEL ====================
  const currentRangeLabel =
    DATE_RANGES.find((r) => r.key === dateRange)?.label || 'Today';

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <div className="p-4 sm:p-8 max-w-[1600px] mx-auto space-y-8">

        {/* ==================== HEADER ==================== */}
        <div className="animate-spring-in">
          <h2 className="text-3xl font-black tracking-tight text-zinc-100">
            System Reports
          </h2>
          <p className="text-zinc-500 text-sm font-medium mt-1">
            Deep-dive into network traffic and conversion data
          </p>
        </div>

        {/* ==================== TABS ==================== */}
        <div className="flex bg-zinc-900/90 p-1.5 rounded-[2rem] border border-zinc-800 w-full sm:w-fit shadow-lg overflow-x-auto no-scrollbar animate-spring-in">
          <div className="flex flex-nowrap gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setCurrentPage(1);
                }}
                className={`px-6 sm:px-8 py-2.5 sm:py-3 rounded-[1.5rem] text-xs sm:text-sm font-black transition-all duration-300 whitespace-nowrap smooth-transition ${
                  activeTab === tab.key
                    ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ==================== FILTERS CARD ==================== */}
        <div className="bg-zinc-900/90 p-5 sm:p-8 rounded-[2.5rem] border border-zinc-800 shadow-lg space-y-6 animate-spring-in">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* Date Range */}
            <div className="space-y-2 relative">
              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                Date Range
              </label>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(openDropdown === 'date' ? null : 'date');
                }}
                className="relative flex items-center justify-between w-full pl-12 pr-4 py-3.5 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm cursor-pointer hover:border-emerald-500/50 transition-all text-zinc-100"
              >
                <Calendar className="w-4 h-4 absolute left-4 text-emerald-400" />
                <span className="capitalize">{currentRangeLabel}</span>
                <ChevronDown
                  className={`w-4 h-4 text-zinc-500 transition-transform ${
                    openDropdown === 'date' ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openDropdown === 'date' && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute z-30 top-full mt-2 w-full bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-spring-in"
                >
                  {DATE_RANGES.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => {
                        setDateRange(r.key);
                        setOpenDropdown(null);
                      }}
                      className={`w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                        dateRange === r.key
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Date Inputs (only when custom selected) */}
            {dateRange === 'custom' && (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3.5 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm text-zinc-100 focus:border-emerald-500 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-3.5 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm text-zinc-100 focus:border-emerald-500 outline-none"
                  />
                </div>
              </>
            )}

            {/* Status + Paid only on clicks/conversions */}
            {activeTab !== 'performance' && (
              <>
                <div className="space-y-2 relative">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Status
                  </label>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(openDropdown === 'status' ? null : 'status');
                    }}
                    className="w-full px-4 py-3.5 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm cursor-pointer flex justify-between items-center text-zinc-100 hover:border-emerald-500/50 transition-all"
                  >
                    {statusFilter}
                    <ChevronDown
                      className={`w-4 h-4 text-zinc-500 transition-transform ${
                        openDropdown === 'status' ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openDropdown === 'status' && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-30 top-full mt-2 w-full bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-spring-in"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s}
                          onClick={() => {
                            setStatusFilter(s);
                            setOpenDropdown(null);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                            statusFilter === s
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2 relative">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">
                    Payment
                  </label>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(openDropdown === 'paid' ? null : 'paid');
                    }}
                    className="w-full px-4 py-3.5 bg-zinc-950/60 border border-zinc-800 rounded-2xl font-bold text-sm cursor-pointer flex justify-between items-center text-zinc-100 hover:border-emerald-500/50 transition-all"
                  >
                    {paidFilter}
                    <ChevronDown
                      className={`w-4 h-4 text-zinc-500 transition-transform ${
                        openDropdown === 'paid' ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openDropdown === 'paid' && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-30 top-full mt-2 w-full bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden animate-spring-in"
                    >
                      {PAID_OPTIONS.map((p) => (
                        <button
                          key={p}
                          onClick={() => {
                            setPaidFilter(p);
                            setOpenDropdown(null);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                            paidFilter === p
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex items-end gap-2">
              <button
                onClick={fetchReports}
                disabled={loading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest active:scale-95 transition-all flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 micro-interaction disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {loading ? 'Loading...' : 'Apply'}
              </button>
              <button
                onClick={handleExportCSV}
                title="Export CSV"
                className="p-3.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-2xl hover:bg-emerald-500 hover:text-zinc-950 transition-all shadow-sm micro-interaction"
              >
                <FileSpreadsheet className="w-4 h-4" />
              </button>
              <button
                onClick={handleExportPDF}
                title="Export PDF"
                className="p-3.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm micro-interaction"
              >
                <FileText className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* ==================== TABLE ==================== */}
        <div className="bg-zinc-900/90 border border-zinc-800 rounded-[2.5rem] shadow-lg overflow-hidden animate-spring-in">

          <div className="overflow-x-auto overflow-y-auto max-h-[70vh] custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[1100px]">
              <thead className="sticky top-0 z-10 bg-zinc-900">
                {activeTab === 'performance' && (
                  <tr className="border-b border-zinc-800 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    <th className="px-8 py-5">Offer Details</th>
                    <th className="px-8 py-5 text-center">Clicks</th>
                    <th className="px-8 py-5 text-center">Unique</th>
                    <th className="px-8 py-5 text-center">Conv</th>
                    <th className="px-8 py-5 text-center">CR %</th>
                    <th className="px-8 py-5 text-right">Earnings</th>
                  </tr>
                )}

                {activeTab === 'clicks' && (
                  <tr className="border-b border-zinc-800 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    <th className="px-6 py-5">TID</th>
                    <th className="px-6 py-5">Offer</th>
                    <th className="px-6 py-5">User</th>
                    <th className="px-6 py-5">Event</th>
                    <th className="px-6 py-5 text-center">Status</th>
                    <th className="px-6 py-5 text-center">Paid</th>
                    <th className="px-6 py-5">Device</th>
                    <th className="px-6 py-5 text-right">Time</th>
                  </tr>
                )}

                {activeTab === 'conversions' && (
                  <tr className="border-b border-zinc-800 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                    <th className="px-6 py-5">TID</th>
                    <th className="px-6 py-5">Offer</th>
                    <th className="px-6 py-5">User</th>
                    <th className="px-6 py-5">Event</th>
                    <th className="px-6 py-5 text-right">Amount</th>
                    <th className="px-6 py-5 text-center">Status</th>
                    <th className="px-6 py-5 text-center">Paid</th>
                    <th className="px-6 py-5 text-right">Conv Time</th>
                  </tr>
                )}
              </thead>

              <tbody className="divide-y divide-zinc-800/60">
                {/* Loading Skeleton */}
                {loading &&
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(activeTab === 'performance' ? 6 : 8)].map((__, j) => (
                        <td key={j} className="px-6 py-5">
                          <div className="h-4 bg-zinc-800 rounded shimmer-bg" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {/* Empty state */}
                {!loading && paginatedData.length === 0 && (
                  <tr>
                    <td
                      colSpan={activeTab === 'performance' ? 6 : 8}
                      className="px-6 py-16 text-center"
                    >
                      <p className="text-sm font-bold text-zinc-500 italic">
                        No records found for the selected filters.
                      </p>
                    </td>
                  </tr>
                )}

                {/* Performance rows */}
                {!loading &&
                  activeTab === 'performance' &&
                  paginatedData.map((row) => (
                    <tr
                      key={row.offer_id}
                      className="hover:bg-emerald-500/[0.03] transition-colors group"
                    >
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          {renderAvatar(row.logo, row.offer_name)}
                          <div className="min-w-0">
                            <p className="font-bold text-zinc-100 group-hover:text-emerald-400 transition-colors truncate">
                              {row.offer_name}
                            </p>
                            <p className="text-[10px] font-bold text-zinc-500">
                              #{row.offer_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-center font-bold text-emerald-400">
                        {row.clicks}
                      </td>
                      <td className="px-8 py-5 text-center font-bold text-zinc-400">
                        {row.unique_clicks}
                      </td>
                      <td className="px-8 py-5 text-center font-bold text-amber-400">
                        {row.conversion}
                      </td>
                      <td className="px-8 py-5 text-center font-black text-zinc-100">
                        {row.cr_rate}%
                      </td>
                      <td className="px-8 py-5 text-right font-black text-emerald-400">
                        ₹{row.earning}
                      </td>
                    </tr>
                  ))}

                {/* Clicks rows */}
                {!loading &&
                  activeTab === 'clicks' &&
                  paginatedData.map((row, idx) => (
                    <tr
                      key={row.tid + idx}
                      className="hover:bg-emerald-500/[0.03] transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-[11px] font-bold text-zinc-400 truncate block max-w-[160px]">
                          {row.tid}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {renderAvatar(row.logo, row.offer_name)}
                          <span className="font-bold text-zinc-100 text-xs truncate max-w-[140px]">
                            {row.offer_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-zinc-200 truncate max-w-[140px]">
                          {row.user_name || '—'}
                        </p>
                        <p className="text-[10px] text-zinc-500 truncate max-w-[140px]">
                          {row.user_upi}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-zinc-300 capitalize">
                          {row.event_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">{statusBadge(row.valid)}</td>
                      <td className="px-6 py-4 text-center">
                        {String(row.paid) === '1' ? (
                          <span className="text-[10px] font-black text-emerald-400 uppercase">
                            Paid
                          </span>
                        ) : (
                          <span className="text-[10px] font-black text-zinc-500 uppercase">
                            Unpaid
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-zinc-300">{row.device}</p>
                        <p className="text-[10px] text-zinc-500">
                          {row.os} · {row.browser}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-[11px] font-mono text-zinc-400 whitespace-nowrap">
                          {row.click_time}
                        </span>
                      </td>
                    </tr>
                  ))}

                {/* Conversions rows */}
                {!loading &&
                  activeTab === 'conversions' &&
                  paginatedData.map((row, idx) => (
                    <tr
                      key={row.tid + idx}
                      className="hover:bg-emerald-500/[0.03] transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-[11px] font-bold text-zinc-400 truncate block max-w-[160px]">
                          {row.tid}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {renderAvatar(row.logo, row.offer_name)}
                          <span className="font-bold text-zinc-100 text-xs truncate max-w-[140px]">
                            {row.offer_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-zinc-200 truncate max-w-[140px]">
                          {row.user_name || '—'}
                        </p>
                        <p className="text-[10px] text-zinc-500 truncate max-w-[140px]">
                          {row.user_upi}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs font-bold text-zinc-300 capitalize">
                          {row.event_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-black text-emerald-400">
                        ₹{row.user_amount}
                      </td>
                      <td className="px-6 py-4 text-center">{statusBadge(row.valid)}</td>
                      <td className="px-6 py-4 text-center">
                        {String(row.paid) === '1' ? (
                          <span className="text-[10px] font-black text-emerald-400 uppercase">
                            Paid
                          </span>
                        ) : (
                          <span className="text-[10px] font-black text-zinc-500 uppercase">
                            Unpaid
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-[11px] font-mono text-zinc-400 whitespace-nowrap">
                          {row.conv_time}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* ==================== PAGINATION FOOTER ==================== */}
          {activeTab !== 'performance' && filteredData.length > 0 && (
            <div className="p-6 bg-zinc-950/40 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">
                Showing {(currentPage - 1) * ROWS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * ROWS_PER_PAGE, filteredData.length)} of{' '}
                {filteredData.length} results
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm micro-interaction"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    // Compact pagination: show first, last, current +/- 1
                    if (
                      totalPages > 7 &&
                      pageNum !== 1 &&
                      pageNum !== totalPages &&
                      Math.abs(pageNum - currentPage) > 1
                    ) {
                      if (pageNum === 2 || pageNum === totalPages - 1) {
                        return (
                          <span
                            key={pageNum}
                            className="px-2 py-2 text-xs font-black text-zinc-600"
                          >
                            …
                          </span>
                        );
                      }
                      return null;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all ${
                          currentPage === pageNum
                            ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                            : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm micro-interaction"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Performance tab footer — simple row count */}
          {activeTab === 'performance' && !loading && filteredData.length > 0 && (
            <div className="p-6 bg-zinc-950/40 border-t border-zinc-800">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest text-center">
                Showing all {filteredData.length} offer{filteredData.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminReports;